/**
 * Титры и строка ввода ПОЛНОГО экрана звонка (Антон 2026-09-12 08:34:
 * «во время звонка я / любой участник могу писать в комнату и для других
 * участников сообщения отобразятся в виде титров или балконов поверх
 * основного экрана звонка»).
 *
 * Вынесено из RoomView.tsx: экран звонка и так перегружен, а здесь —
 * самостоятельная механика с таймерами и дедупликацией, которой место
 * в отдельном файле. В МИНИ-фрейм (CallMiniFrame в App.tsx) это НЕ
 * добавляется — титры делаются только для полного экрана (постановка).
 *
 * ДОПОЛНЕНИЕ 2026-09-12 09:20: ОТДЕЛЬНАЯ СТРОКА ВВОДА внутри экрана
 * звонка ОТМЕНЕНА Антоном («чтобы работали титры, надо убедиться, что ты
 * можешь писать в ту же комнату» → сворачивание звонка в мини-фрейм к
 * обычному композеру вместо второго поля ввода). Здесь остались ТОЛЬКО
 * титры; вход в переписку — кнопка «написать» в контролах CallScreen.
 *
 * ДВА КАПКАНА (из постановки, оба учтены ниже):
 *  1. Sync отдаёт СТАРЫЕ события комнаты как новые. При входе в звонок
 *     нельзя показывать историю в лицо: компонент монтируется вместе с
 *     CallScreen (state === "in-call"), и в момент монтирования ВСЕ
 *     события ленты заносятся в «уже виденные» + фиксируется метка
 *     времени входа. Новым считается только то, чего нет в начальном
 *     снимке И чей ts >= метки входа (локальное эхо своего — всегда,
 *     у него pending).
 *  2. Своё сообщение не должно показаться дважды (локальное эхо +
 *     подтверждение с сервера — у них РАЗНЫЕ eventId). Дедупликация по
 *     (senderId + body) в окне 10 секунд: подтверждение эха приходит
 *     через доли секунды после эха с тем же текстом от того же автора
 *     и отбрасывается.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useT } from "../i18n";

import type { TimelineItem } from "../matrix/timeline";
import { radius, space } from "./theme";

/** Время жизни титра до начала исчезновения (мс). Постановка: 7 секунд. */
const CAPTION_TTL_MS = 7000;
/** Длительность плавного исчезновения (синхронизирована с transition). */
const CAPTION_FADE_MS = 300;
/** Максимум титров одновременно — больше превращается в стену текста. */
const CAPTION_MAX = 3;
/** Окно дедупликации эхо→подтверждение по (senderId+body), мс. */
const DEDUP_WINDOW_MS = 10_000;

/** Один титр на экране. */
interface Caption {
  /** Уникальный ключ — eventId события (НЕ индекс: индексы едут при
      вытеснении и анимация исчезновения прилипала бы к чужому титру). */
  id: string;
  author: string;
  text: string;
  /** true — запущено плавное исчезновение (opacity 0 за 300 мс до снятия). */
  leaving: boolean;
}

/**
 * Титры поверх видео: входящие сообщения комнаты во время звонка.
 *
 * Источник — ГОТОВАЯ лента items из RoomView (постановка п.3: свою
 * подписку на timeline не делаем — это прямой путь к дублям и старым
 * событиям). items приходит пропом, компонент только выбирает из него
 * новые сообщения.
 */
export function CallCaptions({ items }: { items: TimelineItem[] }) {
  const t = useT();
  const [captions, setCaptions] = useState<Caption[]>([]);
  /**
   * eventId всего, что УЖЕ не нужно показывать: начальный снимок ленты
   * на момент входа в звонок + всё показанное. Set в ref, а не в state:
   * его изменение не должно перерисовывать.
   */
  const seenRef = useRef<Set<string>>(new Set());
  /**
   * Дедупликация эхо→подтверждение: "senderId\u0000body" → ts показа.
   * Отдельно от seenRef, потому что у эха и подтверждения РАЗНЫЕ eventId.
   */
  const shownContentRef = useRef<Map<string, number>>(new Map());
  /** Метка входа в звонок: события старше неё — история, не титры. */
  const enteredAtRef = useRef<number>(0);
  /** Таймеры каждого титра: пары (fade, remove). При размонтировании
      (выход из звонка) ВСЕ гасятся — иначе setState после
      размонтирования и утечка (проверка из постановки). */
  const timersRef = useRef<Map<string, { fade: ReturnType<typeof setTimeout>; remove: ReturnType<typeof setTimeout> }>>(new Map());

  const clearTimersOf = useCallback((id: string) => {
    const t = timersRef.current.get(id);
    if (!t) return;
    clearTimeout(t.fade);
    clearTimeout(t.remove);
    timersRef.current.delete(id);
  }, []);

  // Начальный снимок: один раз при монтировании (= вход в звонок).
  // items в deps нет сознательно — нам нужен снимок ИМЕННО на момент
  // монтирования; дальше новые события ловит эффект ниже.
  useEffect(() => {
    enteredAtRef.current = Date.now();
    for (const it of items) seenRef.current.add(it.eventId);
    const timers = timersRef.current;
    return () => {
      // Выход из звонка / размонтирование: гасим ВСЕ таймеры титров.
      for (const t of timers.values()) {
        clearTimeout(t.fade);
        clearTimeout(t.remove);
      }
      timers.clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Выбор новых сообщений из обновлённой ленты.
  useEffect(() => {
    const enteredAt = enteredAtRef.current;
    const fresh: Caption[] = [];

    for (const it of items) {
      if (seenRef.current.has(it.eventId)) continue;
      // Системные строки (входы/выходы, смены аватара) и карточки звонка
      // в титры НЕ идут — только сообщения (постановка).
      if (it.system || it.call) {
        // Отмечаем как виденное, чтобы не перепроверять на каждом рендере.
        seenRef.current.add(it.eventId);
        continue;
      }
      // Отсечка истории: событие старше входа в звонок — НЕ показываем,
      // даже если в начальный снимок оно не попало (догрузка истории
      // вверх во время звонка добавляет в items старые события!).
      // Локальное эхо (pending) пропускаем всегда: оно по определению
      // создано сейчас, на этом клиенте.
      if (!it.pending && it.ts < enteredAt) {
        seenRef.current.add(it.eventId);
        continue;
      }
      // Неотправленное (failed) не показываем: реплики на ходу — про
      // сказанное, а не про зависший черновик.
      if (it.failed) {
        seenRef.current.add(it.eventId);
        continue;
      }
      // Текст титра: вложения рисуем строкой «вложение» — картинки
      // поверх видео не показываем (постановка).
      const text = it.attachment ? t("message.attachment") : it.body.trim();
      if (!text) {
        seenRef.current.add(it.eventId);
        continue;
      }
      // Дедупликация эхо→подтверждение: тот же автор и тот же текст уже
      // показывался в окне 10 секунд — это серверное подтверждение своего
      // эха (или ретрай), второй титр не нужен. eventId у них разные.
      const contentKey = `${it.senderId}\u0000${text}`;
      const shownAt = shownContentRef.current.get(contentKey);
      if (shownAt !== undefined && Date.now() - shownAt < DEDUP_WINDOW_MS) {
        seenRef.current.add(it.eventId);
        continue;
      }

      seenRef.current.add(it.eventId);
      shownContentRef.current.set(contentKey, Date.now());
      fresh.push({ id: it.eventId, author: it.senderName, text, leaving: false });
    }

    if (fresh.length === 0) return;

    setCaptions((prev) => {
      let next = [...prev, ...fresh];
      // Вытеснение: максимум CAPTION_MAX, новые снизу, старые уходят
      // сверху — их таймеры гасим, чтобы не тронули state позже.
      if (next.length > CAPTION_MAX) {
        const evicted = next.slice(0, next.length - CAPTION_MAX);
        for (const c of evicted) clearTimersOf(c.id);
        next = next.slice(next.length - CAPTION_MAX);
      }
      return next;
    });

    // Таймеры жизни каждого нового титра: 6.7 с → начать исчезновение,
    // 7 с → снять с экрана. Два таймера вместо интервала-«часов»: проще
    // и без лишних рендеров раз в секунду.
    for (const c of fresh) {
      const fade = setTimeout(() => {
        setCaptions((prev) => prev.map((p) => (p.id === c.id ? { ...p, leaving: true } : p)));
      }, CAPTION_TTL_MS - CAPTION_FADE_MS);
      const remove = setTimeout(() => {
        timersRef.current.delete(c.id);
        setCaptions((prev) => prev.filter((p) => p.id !== c.id));
      }, CAPTION_TTL_MS);
      timersRef.current.set(c.id, { fade, remove });
    }
  }, [items, clearTimersOf]);

  if (captions.length === 0) return null;

  return (
    <div
      data-testid="call-captions"
      style={{
        position: "absolute",
        // Нижняя треть, НАД рядом контролов: снизу отступ на высоту
        // ряда кнопок (~64px: 48px кнопка + паддинги) + воздух.
        // Абсолютные пиксели: контролы фиксированной высоты, вычислять
        // их через ref дороже, чем держать константу согласованной.
        bottom: 84,
        left: "50%",
        transform: "translateX(-50%)",
        // Колонкой снизу вверх: НОВЫЙ титр появляется снизу и выталкивает
        // старые вверх (постановка «новые вытесняют старые снизу вверх»).
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: space.sm,
        maxWidth: "min(560px, 80%)",
        // Клики сквозь зазоры между титрами проходят к видео; сами титры
        // текста pointer-events не включаем — выделять текст в титре
        // незачем, полный текст читается в ленте после звонка.
        pointerEvents: "none",
        zIndex: 2,
      }}
    >
      {captions.map((c) => (
        <div
          key={c.id}
          data-testid="call-caption"
          style={{
            // Полупрозрачная тёмная подложка со скруглением, светлый
            // текст — читаемо поверх любого видео (постановка).
            background: "rgba(0,0,0,0.62)",
            color: "#fff",
            borderRadius: radius.md,
            padding: "6px 12px",
            fontSize: 14,
            lineHeight: 1.4,
            maxWidth: "100%",
            // Длинный текст: максимум 3 строки, дальше многоточие.
            // Полный текст — в ленте после звонка (постановка).
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 3,
            overflow: "hidden",
            wordBreak: "break-word",
            opacity: c.leaving ? 0 : 1,
            transition: `opacity ${CAPTION_FADE_MS}ms ease`,
          }}
        >
          <b>{c.author}:</b> {c.text}
        </div>
      ))}
    </div>
  );
}

